import classNames from 'classnames';
import { FieldProps, useField } from 'informed';
import { useEffect, useRef, useState } from 'react';
import { FavouriteIcon } from '@tymbe/ui-icons';

type ComboBoxProps<O, V> = {
  getOptionLabel?: (option: O) => string;
  getOptionValue?: (option: O) => V;
  getValueOption?: (value: V, options: O[]) => O | undefined;
  options?: O[];
  placeholder?: string;
  className?: string;
  menuClassName?: string;
  itemClassName?: string;
  inputClassName?: string;
  footerClassName?: string;
  placeholderClassName?: string;
};

const ComboBox = <O, T, Fields extends object>(props: FieldProps<ComboBoxProps<O, T>, T, Fields>) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const {
    fieldState,
    fieldApi,
    render,
    userProps,
  } = useField<ComboBoxProps<O, T>, T>({ ...props });

  const {
    getOptionLabel = (option?: O) => String(option ?? ''),
    getValueOption = (value) => value as unknown as O,
    getOptionValue = (option: O) => option as unknown as T,
    options = [],
    placeholder,
    className,
    menuClassName,
    itemClassName,
    inputClassName,
    footerClassName,
    placeholderClassName = '',
  } = userProps;
  const currentOption = getValueOption(fieldState.value, options);
  const [search, setSearch] = useState(currentOption ? getOptionLabel(currentOption) : placeholder || '');
  const [openAllOptions, setOpenAllOptions] = useState(false)
  useEffect(() => {
    if (inputRef.current === document.activeElement) {
      return;
    }
    setSearch(currentOption ? getOptionLabel(currentOption) : placeholder || '');
  }, [currentOption, getOptionLabel, placeholder]);

  const filteredOptions = options.filter((option) => getOptionLabel(option)
    .toLowerCase()
    .includes(search.toLowerCase()));

  const exactIdMatch = options.find(option => 
    String(getOptionValue(option)).toLowerCase() === search.toLowerCase()
  );

  const reorderedOptions = exactIdMatch 
    ? [exactIdMatch, ...filteredOptions.filter(option => option !== exactIdMatch)]
    : filteredOptions;

  const filteredOptionsSliced = openAllOptions ? reorderedOptions : reorderedOptions.slice(0, 5)

  return render(
    <div className={classNames('relative', className)}>
      <input
        className={classNames(inputClassName, 
          {[placeholderClassName]: (currentOption && getOptionLabel(currentOption) === placeholder) || !currentOption}
        )}
        onFocus={() => {
          setIsOpen(true);
          setSearch('');
        }}
        onBlur={() => {
          setIsOpen(false);
          setOpenAllOptions(false);
          setSearch(currentOption ? getOptionLabel(currentOption) : placeholder || '');
        }}
        ref={inputRef}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        title={search}
      />
      <div         
        className={classNames('absolute z-50', menuClassName, { 'hidden': !isOpen })}
        onMouseDown={(e) => e.preventDefault()}
      >
        <div
          className={classNames('cursor-pointer', itemClassName)}
          onClick={() => {
            fieldApi.setValue();
            inputRef.current?.blur();
          }}
        >
          {placeholder}
        </div>
        {filteredOptionsSliced.map((option) => (
          <div
            className={classNames('cursor-pointer', itemClassName, { selected: currentOption === option })}
            key={JSON.stringify(getOptionValue(option))}
            onClick={() => {
              fieldApi.setValue(getOptionValue(option));
              inputRef.current?.blur();
            }}
          >
            <span className='truncate'>
              {getOptionLabel(option)}
            </span>
          </div>
        ))}
        {!openAllOptions &&
        <div className={footerClassName}>
          {filteredOptions.length > 5 ?
          <button type='button' onClick={() => setOpenAllOptions(true)} className='flex p-0 items-center'>
            <FavouriteIcon name="chevron_down" className='size-4'/> Zobrazit vše
          </button> : <div/>
          }
          <span>{filteredOptions.length} výsledků</span>
        </div>
        }
      </div>
    </div>
  );
};

export default ComboBox;
